import { useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { DefaultAvatar } from '@/components/business/default-avatar'
import { APP_TITLE } from '@/config/app'
import { Icon } from './icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const APP_SHELL_BRAND_MARK_SRC = '/logo.svg'

export interface SidebarNavItem {
  /** 路由路径 (与 onClick 二选一) */
  to?: string
  /** 点击行为 (与 to 二选一) */
  onClick?: () => void
  label: string
  icon: string
  /** 是否填充态;active 项会被强制为 filled */
  filled?: boolean
  /** 右侧未读徽标数字 */
  badgeCount?: number
}

export interface SidebarHistoryItem {
  id: string
  title: string
  to: string
  state?: unknown
  icon?: string
  active?: boolean
  isPinned?: boolean
}

export interface SidebarHistoryItemActions {
  onPin?: (item: SidebarHistoryItem) => void
  onRename?: (item: SidebarHistoryItem) => void
  onDelete?: (item: SidebarHistoryItem) => void
}

interface AppSidebarProps {
  /** 顶部 brand 区标题 */
  brandTitle?: string
  /** 顶部 brand 区副标题, 例如 "智能法律顾问" / "律师工作台" */
  brandSubtitle?: string
  /** 顶部 brand 区图标; 当前 Figma shell 使用本地下载图标, 该字段保留给后续多工作台扩展 */
  brandIcon?: string
  /** 是否展示菜单项图标; 当前 Figma shell 默认隐藏, 后续可打开 */
  showMenuIcons?: boolean
  /** 是否完全隐藏侧边栏 */
  collapsed?: boolean
  /** 移动端抽屉内发生导航后关闭侧边栏 */
  onNavigate?: () => void
  /** 主导航 */
  items: SidebarNavItem[]
  /** 底部分割区,用于设置等次要项;角色切换会自动追加 (当账号多角色时) */
  footerItems?: SidebarNavItem[]
  /** 顶部 CTA 按钮 (如 "新建对话") */
  primaryAction?: {
    label: string
    icon?: string
    onClick?: () => void
    shortcut?: string
    active?: boolean
    tourId?: string
  }
  /** AI 文书历史会话列表 */
  historyItems?: SidebarHistoryItem[]
  historyItemActions?: SidebarHistoryItemActions
  historyLoading?: boolean
  /** 是否显示历史对话区 */
  showHistory?: boolean
  /** 底部账号入口 */
  account?: {
    name: string
    avatar?: string
    switchEntry?: {
      label: string
      icon?: string
      onClick: () => void
    }
    onProfile?: () => void
    onSettings?: () => void
    onLogout?: () => void
  }
  /** 未登录时的底部入口 */
  guestAction?: {
    label: string
    description?: string
    icon?: string
    disabled?: boolean
    onClick: () => void
  }
}

/**
 * 全局左侧导航
 *
 * 设计参考: ui/.../dashboard 与 user_profile_dashboard 的 sidebar 部分.
 * Active 项通过 useLocation 与 item.to 前缀比对决定 (支持子路径高亮).
 */
export function AppSidebar({
  brandTitle = APP_TITLE,
  brandIcon = APP_SHELL_BRAND_MARK_SRC,
  brandSubtitle = '智能法律顾问',
  showMenuIcons = false,
  collapsed = false,
  items,
  footerItems = [],
  primaryAction,
  historyItems = [],
  historyItemActions,
  historyLoading = false,
  showHistory = true,
  account,
  guestAction,
  onNavigate,
}: AppSidebarProps) {
  const location = useLocation()

  function isActive(to?: string) {
    if (!to) return false
    if (to === location.pathname) return true
    // 只对非根路径做前缀匹配，避免 /u 在所有子路由都高亮
    if (to === '/u' || to === '/lawyer') return false
    return location.pathname.startsWith(to + '/')
  }

  const computedFooter: SidebarNavItem[] = [...footerItems]
  const hasAccountPrimaryActions = Boolean(
    account?.switchEntry || account?.onProfile || account?.onSettings,
  )

  return (
    <aside
      aria-hidden={collapsed}
      className={cn(
        'app-sidebar-surface fixed inset-y-0 left-0 z-40 flex h-dvh shrink-0 flex-col overflow-hidden transition-[width,padding,transform] duration-300 md:sticky md:top-0 md:h-screen',
        collapsed
          ? 'w-0 -translate-x-full p-0 md:translate-x-0'
          : 'w-[min(var(--spacing-sidebar-width),calc(100vw-3rem))] translate-x-0 md:w-(--spacing-sidebar-width)',
      )}
    >
      {!collapsed && (
        <div className="flex h-full min-h-0 flex-col">
          <div className="relative h-[58px] shrink-0">
            <BrandMark src={brandIcon} />
            <h1 className="absolute top-[25px] left-[60px] truncate text-[16px] leading-[22px] font-normal text-black">
              {brandTitle}
            </h1>
            <p className="absolute top-7 left-[146px] truncate text-[12px] leading-4 font-normal text-[#707070]">
              {brandSubtitle}
            </p>
          </div>

          {primaryAction && (
            <button
              type="button"
              data-tour={primaryAction.tourId}
              onClick={() => {
                primaryAction.onClick?.()
                onNavigate?.()
              }}
              title={primaryAction.label}
              aria-label={primaryAction.label}
              aria-current={primaryAction.active ? 'page' : undefined}
              className={cn(
                'mx-3 flex h-[42px] w-[calc(100%-1.5rem)] items-center rounded-[10px] pr-5 pl-[48px] text-left text-[16px] leading-[22px] transition-colors',
                primaryAction.active
                  ? 'bg-white font-medium text-black'
                  : 'font-normal text-black/90 hover:bg-white/70 hover:text-black dark:text-black/90 dark:hover:text-black',
              )}
            >
              <span className="truncate">{primaryAction.label}</span>
              {primaryAction.shortcut && (
                <span className="ml-auto rounded-full bg-black/[0.04] px-1.5 py-0.5 text-[10px] leading-none text-[#707070]">
                  {primaryAction.shortcut}
                </span>
              )}
            </button>
          )}

          {(items.length > 0 || computedFooter.length > 0) && (
            <nav className="flex shrink-0 flex-col">
              {items.map((it) => (
                <SidebarRow
                  key={it.label}
                  item={it}
                  active={isActive(it.to)}
                  showIcon={showMenuIcons}
                  onNavigate={onNavigate}
                />
              ))}
              {computedFooter.map((it) => (
                <SidebarRow
                  key={it.label}
                  item={it}
                  active={isActive(it.to)}
                  showIcon={showMenuIcons}
                  onNavigate={onNavigate}
                />
              ))}
            </nav>
          )}

          {showHistory ? (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="h-8 pt-[9px] pl-[60px] text-[12px] leading-4 font-normal text-[#707070]">
                历史对话
              </div>
              {historyLoading && (
                <div className="flex items-center gap-2 py-2 pr-6 pl-[60px] text-xs text-[#707070]">
                  <Icon name="autorenew" className="animate-spin" />
                  加载中...
                </div>
              )}
              {!historyLoading && historyItems.length === 0 && (
                <p className="py-2 pr-6 pl-[60px] text-xs text-[#707070]">暂无历史对话</p>
              )}
              <div className="flex flex-col pb-3">
                {historyItems.map((item) => (
                  <SidebarHistoryRow
                    key={item.id}
                    item={item}
                    actions={historyItemActions}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1" />
          )}

          {account && (
            <div className="mt-auto shrink-0 px-5 pb-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="focus-visible:ring-primary/25 flex h-11 w-full items-center gap-2 rounded-full px-2 text-left transition-colors hover:bg-black/[0.04] focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <AvatarBubble name={account.name} src={account.avatar} size="sm" />
                    <span className="text-on-surface min-w-0 flex-1 truncate text-sm font-medium">
                      {account.name}
                    </span>
                    <Icon name="chevron_right" className="text-outline text-sm" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  align="start"
                  sideOffset={12}
                  collisionPadding={12}
                  className="w-48"
                >
                  {account.switchEntry && (
                    <DropdownMenuItem
                      onClick={account.switchEntry.onClick}
                      onSelect={onNavigate}
                      className="text-on-surface flex h-9 cursor-pointer gap-3 px-3"
                    >
                      <Icon name={account.switchEntry.icon || 'sync'} className="text-base" />
                      <span>{account.switchEntry.label}</span>
                    </DropdownMenuItem>
                  )}
                  {account.switchEntry && (account.onProfile || account.onSettings) && (
                    <DropdownMenuSeparator className="bg-outline-variant/70 my-2" />
                  )}
                  {account.onProfile && (
                    <DropdownMenuItem
                      onClick={account.onProfile}
                      onSelect={onNavigate}
                      className="text-on-surface flex h-9 cursor-pointer gap-3 px-3"
                    >
                      <Icon name="person" className="text-base" />
                      <span>我的</span>
                    </DropdownMenuItem>
                  )}
                  {account.onSettings && (
                    <DropdownMenuItem
                      onClick={account.onSettings}
                      onSelect={onNavigate}
                      className="text-on-surface flex h-9 cursor-pointer gap-3 px-3"
                    >
                      <Icon name="settings" className="text-base" />
                      <span>设置</span>
                    </DropdownMenuItem>
                  )}
                  {hasAccountPrimaryActions && account.onLogout && (
                    <DropdownMenuSeparator className="bg-outline-variant/70 my-2" />
                  )}
                  {account.onLogout && (
                    <DropdownMenuItem
                      onClick={account.onLogout}
                      onSelect={onNavigate}
                      className="text-error focus:text-error focus:bg-error-container/40 flex h-9 cursor-pointer gap-3 px-3"
                    >
                      <Icon name="logout" className="text-base" />
                      <span>退出登录</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {!account && guestAction && (
            <div className="mt-auto shrink-0 px-5 pb-4">
              <button
                type="button"
                disabled={guestAction.disabled}
                onClick={() => {
                  guestAction.onClick()
                  onNavigate?.()
                }}
                title={guestAction.disabled ? '请等待当前回答完成' : undefined}
                className="focus-visible:ring-primary/25 flex h-12 w-full items-center gap-2 rounded-full px-2 text-left transition-colors hover:bg-black/[0.04] focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <div className="glass-field text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                  <Icon name={guestAction.icon || 'person'} className="text-base" />
                </div>
                <span className="min-w-0 flex-1">
                  <span className="text-on-surface block truncate text-sm font-medium">
                    {guestAction.label}
                  </span>
                  {guestAction.description && (
                    <span className="text-outline block truncate text-xs">
                      {guestAction.description}
                    </span>
                  )}
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  )
}

function SidebarHistoryRow({
  item,
  actions,
  onNavigate,
}: {
  item: SidebarHistoryItem
  actions?: SidebarHistoryItemActions
  onNavigate?: () => void
}) {
  const hasActions = Boolean(actions?.onPin || actions?.onRename || actions?.onDelete)
  const suppressHistoryMenuTriggerFocusRef = useRef(false)

  function handleHistoryMenuActionSelect(action?: (item: SidebarHistoryItem) => void) {
    suppressHistoryMenuTriggerFocusRef.current = true
    action?.(item)
  }

  return (
    <div
      className={cn(
        'group mx-3 flex h-8 min-w-0 items-center rounded-[10px] text-[14px] leading-5 transition-colors',
        item.active
          ? 'bg-white font-medium text-black'
          : 'font-normal text-black/80 hover:bg-white/70 hover:text-black',
      )}
    >
      <Link
        to={item.to}
        state={item.state}
        onClick={onNavigate}
        title={item.title}
        aria-current={item.active ? 'page' : undefined}
        className="flex h-full min-w-0 flex-1 items-center pr-1 pl-[48px]"
      >
        <span className="truncate">{item.title}</span>
      </Link>
      {(hasActions || item.isPinned) && (
        <div className="relative mr-1 flex size-7 shrink-0 items-center justify-center">
          {item.isPinned && (
            <Icon
              name="push_pin"
              className={cn(
                'pointer-events-none absolute text-[14px] text-black/45 transition-opacity',
                hasActions ? 'opacity-100 group-hover:opacity-0' : 'opacity-100',
              )}
            />
          )}
          {hasActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`打开${item.title}的会话菜单`}
                  className="absolute flex size-7 shrink-0 items-center justify-center rounded-full text-black/45 opacity-0 transition-colors group-hover:opacity-100 hover:bg-black/[0.06] hover:text-black focus-visible:bg-black/[0.06] focus-visible:text-black focus-visible:opacity-100 focus-visible:outline-none data-[state=open]:bg-black/[0.06] data-[state=open]:text-black data-[state=open]:opacity-100"
                >
                  <Icon name="more_vert" className="text-[16px]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="start"
                sideOffset={8}
                collisionPadding={12}
                onCloseAutoFocus={(event) => {
                  if (!suppressHistoryMenuTriggerFocusRef.current) return
                  event.preventDefault()
                  suppressHistoryMenuTriggerFocusRef.current = false
                }}
                className="w-36 rounded-[10px] p-1"
              >
                <DropdownMenuGroup>
                  {actions?.onPin && (
                    <DropdownMenuItem
                      onSelect={() => handleHistoryMenuActionSelect(actions.onPin)}
                      className="flex h-9 cursor-pointer gap-3 rounded-[8px] px-3"
                    >
                      <Icon name="push_pin" className="text-base" />
                      <span>{item.isPinned ? '取消置顶' : '置顶'}</span>
                    </DropdownMenuItem>
                  )}
                  {actions?.onRename && (
                    <DropdownMenuItem
                      onSelect={() => handleHistoryMenuActionSelect(actions.onRename)}
                      className="flex h-9 cursor-pointer gap-3 rounded-[8px] px-3"
                    >
                      <Icon name="edit_note" className="text-base" />
                      <span>重命名</span>
                    </DropdownMenuItem>
                  )}
                  {actions?.onDelete && (
                    <DropdownMenuItem
                      onSelect={() => handleHistoryMenuActionSelect(actions.onDelete)}
                      className="flex h-9 cursor-pointer gap-3 rounded-[8px] px-3 text-[#ff3b30] hover:bg-[#ff3b30]/10 focus:bg-[#ff3b30]/10 focus:text-[#ff3b30] data-[highlighted]:bg-[#ff3b30]/10 data-[highlighted]:text-[#ff3b30]"
                    >
                      <Icon name="delete" className="text-base" />
                      <span>删除</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  )
}

function SidebarRow({
  item,
  active,
  showIcon,
  onNavigate,
}: {
  item: SidebarNavItem
  active: boolean
  showIcon: boolean
  onNavigate?: () => void
}) {
  const className = cn(
    'mx-3 flex h-[42px] w-[calc(100%-1.5rem)] items-center rounded-[10px] pr-5 text-[16px] leading-[22px] transition-colors',
    showIcon ? 'gap-3 pl-4' : 'pl-[48px]',
    active
      ? 'bg-white font-medium text-black'
      : 'font-normal text-black/90 hover:bg-white/70 hover:text-black dark:text-black/90 dark:hover:text-black',
  )
  const badgeText = clampSidebarBadgeCount(item.badgeCount)

  const inner = (
    <>
      {showIcon && (
        <Icon
          name={item.icon}
          filled={active || item.filled}
          className={cn('shrink-0 text-[20px]', active ? 'text-black' : 'text-black/70')}
        />
      )}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {badgeText && (
        <span className="bg-error text-on-error ml-auto flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-semibold">
          {badgeText}
        </span>
      )}
    </>
  )

  if (item.to) {
    return (
      <Link
        to={item.to}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={className}
      >
        {inner}
      </Link>
    )
  }
  return (
    <button
      type="button"
      onClick={() => {
        item.onClick?.()
        onNavigate?.()
      }}
      className={cn(className, 'text-left')}
    >
      {inner}
    </button>
  )
}

function clampSidebarBadgeCount(count: number | undefined): string {
  if (!count || count <= 0) return ''
  return count > 99 ? '99+' : String(count)
}

function BrandMark({ src }: { src: string }) {
  return <img src={src} alt="" className="absolute top-6 left-7 h-6 w-6" aria-hidden="true" />
}

function AvatarBubble({
  name,
  src,
  size = 'md',
}: {
  name: string
  src?: string
  size?: 'sm' | 'md'
}) {
  const sizeClass = size === 'sm' ? 'h-7 w-7 text-xs' : 'h-9 w-9 text-sm'

  return (
    <div
      className={cn(
        'glass-field text-on-surface-variant flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        sizeClass,
      )}
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <DefaultAvatar label={name || '默认头像'} />
      )}
    </div>
  )
}
